// src/RecipeDB/FavoriteRecipes.js
import React, { useEffect, useState, useCallback } from 'react';
import { getFavoriteRecipes } from './recipeService';
import { getRecetariosOfUser, deleteRecetario, createRecetario } from './recetarioService';
import SavedRecipeCard from './SavedRecipeCard';
import RecetarioCard from './RecetarioCard';
import getRecipeId from '../utils/getRecipeId';
import { useFavoriteRecipes } from '../hooks/useFavoriteRecipes';
import { useAuth } from '../Auth/AuthContext';
import { useToast } from '../context/ToastContext';
import './FavoriteRecipes.css';

const FavoriteRecipes = ({ userId }) => {
  const { userId: authUserId } = useAuth();
  const { showToast } = useToast();
  const { favoriteRecipesIds, removeFromFavorites } = useFavoriteRecipes(userId);

  const [favoriteRecipes, setFavoriteRecipes] = useState([]);
  const [recetarios, setRecetarios]           = useState([]);
  const [loadingRecetarios, setLoadingRecetarios] = useState(true);
  const [showCreateRecetario, setShowCreateRecetario] = useState(false);
  const [newRecetarioNombre, setNewRecetarioNombre] = useState('');
  const [newRecetarioDesc, setNewRecetarioDesc] = useState('');
  const [creatingRecetario, setCreatingRecetario] = useState(false);

  // Cargar recetas guardadas
  useEffect(() => {
    if (!userId) return;
    getFavoriteRecipes(userId).then(setFavoriteRecipes);
  }, [userId, favoriteRecipesIds]);

  // Cargar recetarios
  const loadRecetarios = useCallback(() => {
    if (!userId) return;
    setLoadingRecetarios(true);
    getRecetariosOfUser(userId)
      .then(setRecetarios)
      .finally(() => setLoadingRecetarios(false));
  }, [userId]);
  useEffect(() => {
    loadRecetarios();
  }, [loadRecetarios]);

  // IDs de recetas que pertenecen a algún recetario
  const inRecetarioIds = new Set(recetarios.flatMap(r => r.recetaIds || []));

  // Recetas sueltas = guardadas que NO están en ningún recetario
  const looseRecipes = favoriteRecipes.filter(r => {
    const id = getRecipeId(r);
    return !inRecetarioIds.has(id);
  });

  const handleDeleteRecetario = async (recetario) => {
    if (!window.confirm(`¿Eliminar el recetario "${recetario.nombre}"? Las recetas no se borran.`)) return;
    try {
      await deleteRecetario(recetario.id, authUserId);
      setRecetarios(prev => prev.filter(r => r.id !== recetario.id));
      showToast('Recetario eliminado', 'success');
    } catch {
      showToast('Error al eliminar el recetario', 'error');
    }
  };

  const handleCreateEmptyRecetario = async () => {
    const nombre = newRecetarioNombre.trim();
    if (!nombre) return;

    const ownerId = authUserId || userId;
    if (!ownerId) {
      showToast('No se pudo identificar al usuario', 'error');
      return;
    }

    setCreatingRecetario(true);
    try {
      const recetarioId = await createRecetario(ownerId, {
        nombre,
        descripcion: newRecetarioDesc,
        publica: false,
      });

      const optimisticRecetario = {
        id: recetarioId,
        nombre,
        descripcion: newRecetarioDesc.trim(),
        ownerId,
        coOwnerIds: [],
        recetaIds: [],
        coverImages: [],
        publica: false,
        createdAt: { seconds: Math.floor(Date.now() / 1000) },
      };

      setRecetarios(prev => {
        const deduped = prev.filter(r => r.id !== recetarioId);
        return [optimisticRecetario, ...deduped];
      });

      setShowCreateRecetario(false);
      setNewRecetarioNombre('');
      setNewRecetarioDesc('');
      showToast('Recetario creado', 'success');
      loadRecetarios();
    } catch {
      showToast('Error al crear el recetario', 'error');
    } finally {
      setCreatingRecetario(false);
    }
  };

  return (
    <div className="favorite-recipes-container">
      {/* ── Recetarios ──────────────────────────────────────── */}
      <section className="fav-section">
        <div className="fav-section__header">
          <h2 className="fav-section__title">Mis Recetarios</h2>
          <button
            className="fav-add-btn"
            onClick={() => setShowCreateRecetario(v => !v)}
            title="Crear recetario vacío"
            aria-label="Crear recetario"
          >
            +
          </button>
        </div>

        {showCreateRecetario && (
          <div className="fav-create-inline">
            <input
              type="text"
              placeholder="Nombre del recetario *"
              value={newRecetarioNombre}
              onChange={e => setNewRecetarioNombre(e.target.value)}
              maxLength={60}
              autoFocus
            />
            <textarea
              placeholder="Descripción (opcional)"
              value={newRecetarioDesc}
              onChange={e => setNewRecetarioDesc(e.target.value)}
              maxLength={200}
              rows={2}
            />
            <div className="fav-create-inline__actions">
              <button
                className="fav-create-inline__save"
                onClick={handleCreateEmptyRecetario}
                disabled={creatingRecetario || !newRecetarioNombre.trim()}
              >
                {creatingRecetario ? 'Creando...' : 'Crear'}
              </button>
              <button
                className="fav-create-inline__cancel"
                onClick={() => {
                  setShowCreateRecetario(false);
                  setNewRecetarioNombre('');
                  setNewRecetarioDesc('');
                }}
                disabled={creatingRecetario}
              >
                Cancelar
              </button>
            </div>
          </div>
        )}

        {!loadingRecetarios && recetarios.length === 0 && (
          <p className="fav-empty">Aún no tienes recetarios. Pulsa + para crear el primero.</p>
        )}
        <div className="recetarios-grid">
          {recetarios.map(r => (
            <RecetarioCard
              key={r.id}
              recetario={r}
              isOwner={r.ownerId === authUserId}
              onDelete={handleDeleteRecetario}
            />
          ))}
        </div>
      </section>

      {/* ── Recetas sueltas ─────────────────────────────────── */}
      <section className="fav-section">
        <h2 className="fav-section__title">Recetas guardadas</h2>
        {favoriteRecipes.length === 0 && (
          <p className="fav-empty">No hay recetas guardadas.</p>
        )}
        <div className="feed">
          {looseRecipes.map(recipe => (
            <SavedRecipeCard
              key={getRecipeId(recipe)}
              recipe={recipe}
              onRemove={removeFromFavorites}
              onRecetariosChanged={loadRecetarios}
            />
          ))}
          {/* Recetas que sí están en recetarios pero también se muestran aquí (para poder gestionarlas) */}
          {favoriteRecipes
            .filter(r => inRecetarioIds.has(getRecipeId(r)))
            .map(recipe => (
              <SavedRecipeCard
                key={getRecipeId(recipe)}
                recipe={recipe}
                onRemove={removeFromFavorites}
                onRecetariosChanged={loadRecetarios}
              />
            ))
          }
        </div>
      </section>
    </div>
  );
};

export default FavoriteRecipes;
